import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listUsers, deleteUser } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/users/")({
  component: UsersListPage,
});

function UsersListPage() {
  const list = useServerFn(listUsers);
  const del = useServerFn(deleteUser);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>("");

  const reload = async () => {
    setLoading(true);
    try {
      setUsers((await list({})) as any[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? ""));
    reload();
  }, []);

  const remove = async (u: any) => {
    if (!confirm(`Ta bort ${u.email}?`)) return;
    try {
      await del({ data: { user_id: u.id } });
      toast.success("Borttagen");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <PageHeader title={`Användare (${users.length})`} />
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div
              key={u.id}
              className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold break-all">{u.email}</span>
                  {u.is_admin && (
                    <span className="text-[10px] font-bold uppercase bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                      Admin
                    </span>
                  )}
                  {u.id === currentUserId && (
                    <span className="text-[10px] font-bold uppercase bg-secondary px-2 py-0.5 rounded-full">
                      Du
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Skapad: {new Date(u.created_at).toLocaleString("sv-SE")}{" "}
                  {u.last_sign_in_at &&
                    `· Senast inloggad: ${new Date(u.last_sign_in_at).toLocaleString("sv-SE")}`}
                </p>
              </div>
              <Link
                to="/admin/users/$id"
                params={{ id: u.id }}
                className="p-2.5 rounded-md bg-secondary hover:bg-secondary/70 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
                title="Redigera"
                aria-label="Redigera användare"
              >
                <Pencil className="h-4 w-4" />
              </Link>
              <button
                onClick={() => remove(u)}
                disabled={u.id === currentUserId}
                className="p-2.5 rounded-md bg-destructive text-destructive-foreground disabled:opacity-50 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
                aria-label="Ta bort användare"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {users.length === 0 && (
            <p className="text-sm text-muted-foreground">Inga användare.</p>
          )}
        </div>
      )}
    </>
  );
}
